/**
 * ConverterHistoryPanel — a reusable slide-in side pane listing the last N
 * saved runs of a Data Converter standalone action (Mapping / Data Loader /
 * Reconciliation / Load to Target), letting the user pick one to resume
 * from instead of redoing the work. One shared component backs all four
 * pickers so they look and behave identically; each caller supplies its own
 * entries (already fetched via the matching apiv2 `converterList*History`
 * call) and a `renderEntry` function for the type-specific summary line.
 *
 * Purely presentational — fetching, selection side effects, and "start
 * fresh on project change" belong to the caller.
 */
import { X, History as HistoryIcon, Loader2 } from "lucide-react";

export interface ConverterHistoryPanelProps<T> {
  open: boolean;
  title: string;
  subtitle?: string;
  entries: T[];
  loading?: boolean;
  emptyLabel?: string;
  getKey: (entry: T) => string;
  renderEntry: (entry: T) => React.ReactNode;
  onSelect: (entry: T) => void;
  onClose: () => void;
}

export function ConverterHistoryPanel<T>({
  open,
  title,
  subtitle,
  entries,
  loading = false,
  emptyLabel = "No saved runs yet for this project.",
  getKey,
  renderEntry,
  onSelect,
  onClose,
}: ConverterHistoryPanelProps<T>) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-[1px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <HistoryIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {title}
              </h3>
              {subtitle && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading history...
            </div>
          ) : entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              {emptyLabel}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {entries.map((entry) => (
                <li key={getKey(entry)}>
                  <button
                    type="button"
                    onClick={() => onSelect(entry)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm transition hover:border-blue-400 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-500 dark:hover:bg-slate-700/50"
                  >
                    {renderEntry(entry)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-400 dark:border-slate-700">
          Showing up to the last 10 saved runs · switching projects starts fresh
        </div>
      </div>
    </div>
  );
}
